import React from "react";
import { Box, Typography, Button } from "@mui/material";
import { useTranslation } from "../../hooks/useTranslation";
import ShieldIcon from "@mui/icons-material/Shield";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import NatureIcon from "@mui/icons-material/Nature";

interface FaultStepProps {
  accidentFault: string;
  onChange: (field: string, value: string) => void;
  setDequalified: () => void;
}

const FaultStep: React.FC<FaultStepProps> = ({
  accidentFault,
  onChange,
  setDequalified,
}) => {
  const { t } = useTranslation();

  const options = [
    {
      value: "NO",
      label: t("form.thirdQuestion.option1"),
      icon: ShieldIcon,
      color: {
        main: "#3b82f6",
        light: "#eff6ff",
        border: "#bfdbfe",
        hover: "#dbeafe",
      },
      onClick: () => onChange("accidentFault", "NO"),
    },
    {
      value: "YES",
      label: t("form.thirdQuestion.option2"),
      icon: ReportProblemIcon,
      color: {
        main: "#f97316",
        light: "#fff7ed",
        border: "#fed7aa",
        hover: "#ffedd5",
      },
      onClick: () => onChange("accidentFault", "YES"),
    },
    {
      value: "SINGLE",
      label: t("form.thirdQuestion.option3"),
      icon: NatureIcon,
      color: {
        main: "#64748b",
        light: "#f8fafc",
        border: "#cbd5e1",
        hover: "#f1f5f9",
      },
      onClick: setDequalified,
    },
  ];

  return (
    <Box>
      <Typography
        variant="subtitle2"
        align="center"
        sx={{
          color: "#9ca3af",
          fontWeight: 500,
          mb: 2,
          fontSize: "0.875rem",
        }}
      >
        Question 3 out of 5
      </Typography>

      <Typography
        variant="h4"
        align="center"
        sx={{
          mb: 4,
          fontWeight: 700,
          color: "#1e293b",
          fontSize: { xs: "1.2rem", md: "1.5rem" },
        }}
      >
        3. {t("form.thirdQuestion.question")}
        <span style={{ color: "#ef4444" }}>*</span>
      </Typography>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {options.map((option) => {
          const isSelected = accidentFault === option.value;
          const Icon = option.icon;

          return (
            <Button
              key={option.value}
              onClick={option.onClick}
              sx={{
                padding: "1.5rem",
                borderRadius: "12px",
                border: `2px solid ${
                  isSelected ? option.color.main : option.color.border
                }`,
                backgroundColor: isSelected
                  ? option.color.light
                  : "transparent",
                display: "flex",
                alignItems: "center",
                gap: 2,
                justifyContent: "flex-start",
                textTransform: "none",
                transition: "all 0.2s ease",
                "&:hover": {
                  backgroundColor: option.color.hover,
                  borderColor: option.color.main,
                },
              }}
            >
              <Icon
                sx={{
                  fontSize: 32,
                  color: option.color.main,
                  flexShrink: 0,
                }}
              />
              <Typography
                sx={{
                  fontSize: { xs: "1rem", md: "1.125rem" },
                  fontWeight: 500,
                  color: "#334155",
                  textAlign: "left",
                }}
              >
                {option.label}
              </Typography>
            </Button>
          );
        })}
      </Box>
    </Box>
  );
};

export default FaultStep;
